import type { Hotel, HotelParseResult } from '../../types/hotel';
import { awaitJob } from './asyncJob';
import { API_URL, fetchApi, fetchWithAuth } from './client';

export type { Hotel, HotelParseResult };

export const getHotels = async (): Promise<Hotel[]> => {
  return fetchApi<Hotel[]>(`${API_URL}/hotels`);
};

export const getHotelTags = async (): Promise<{ tags: string[] }> => {
  return fetchApi<{ tags: string[] }>(`${API_URL}/hotels/tags`);
};

export const createHotel = async (body: Omit<Hotel, 'id'>): Promise<Hotel> => {
  return fetchApi<Hotel>(`${API_URL}/hotels`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateHotel = async (id: number, body: Partial<Omit<Hotel, 'id'>>): Promise<Hotel> => {
  return fetchApi<Hotel>(`${API_URL}/hotels/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const deleteHotel = async (id: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/hotels/${id}`, { method: 'DELETE' });
};

export const parseHotelBooking = async (opts: {
  file?: File | null;
  url?: string;
  language?: string;
}): Promise<HotelParseResult> => {
  const form = new FormData();
  if (opts.file) form.append('file', opts.file);
  if (opts.url) form.append('url', opts.url);
  if (opts.language) form.append('language', opts.language);
  const response = await fetchWithAuth(`${API_URL}/hotels/parse`, {
    method: 'POST',
    body: form,
  });
  if (response.status === 401) {
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
  }
  const accepted = (await response.json()) as { jobId?: string };
  if (!accepted?.jobId) {
    throw new Error('No jobId in response');
  }
  return awaitJob<HotelParseResult>(accepted.jobId, { timeoutMs: 180_000 });
};
